import type { Chat, ChatEvent, ChatQueueItem, ChatWsServerMessage } from "@codvero/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useReducer, useRef } from "react";
import { chatsApi, wsUrl } from "@/api/chats.ts";

export interface StreamState {
  chat: Chat | null;
  events: ChatEvent[];
  /** Sequential task list of this chat (server-authoritative). */
  queue: ChatQueueItem[];
  /** Live partial text for the in-flight assistant message (from stream_event deltas). */
  partial: string;
  connected: boolean;
  error: string | null;
  /** The first loaded seq of a long conversation opened on its last turns; 0 = everything is loaded. */
  from: number;
}

/** Adds events in seq order without duplicates. The common case (all newer than what we have) is a plain append:
 *  sorting and scanning the whole list for every event froze long conversations. */
function merge(have: ChatEvent[], add: ChatEvent[]): ChatEvent[] {
  if (!add.length) return have;
  const last = have.length ? have[have.length - 1]!.seq : -Infinity;
  let ordered = true;
  for (let i = 0; i < add.length; i++) if (add[i]!.seq <= (i ? add[i - 1]!.seq : last)) { ordered = false; break; }
  if (ordered) return have.concat(add);
  const bySeq = new Map(have.map((e) => [e.seq, e]));
  for (const e of add) if (!bySeq.has(e.seq)) bySeq.set(e.seq, e);
  return [...bySeq.values()].sort((x, y) => x.seq - y.seq);
}

type Action =
  | { type: "msg"; msg: ChatWsServerMessage }
  | { type: "conn"; connected: boolean }
  | { type: "patch"; fn: (c: Chat) => Chat }
  | { type: "earlier"; from: number; events: ChatEvent[] }
  | { type: "reset" };

function reduce(s: StreamState, a: Action): StreamState {
  switch (a.type) {
    case "reset":
      return { chat: null, events: [], queue: [], partial: "", connected: false, error: null, from: 0 };
    case "earlier":
      return { ...s, from: a.from, events: merge(a.events, s.events) };
    case "patch":
      return s.chat ? { ...s, chat: a.fn(s.chat) } : s;
    case "conn":
      // chunks sent while disconnected are not replayed: drop the half-written bubble, the full message follows
      return { ...s, connected: a.connected, partial: a.connected ? s.partial : "" };
    case "msg": {
      const m = a.msg;
      if (m.type === "chat") return { ...s, chat: m.chat, partial: m.chat.status === "running" ? s.partial : "" };
      if (m.type === "error") return { ...s, error: m.code };
      if (m.type === "queue") return { ...s, queue: m.items };
      if (m.type === "window") return { ...s, from: m.from };
      if (m.type === "events") {
        const lastProvider = [...m.events].reverse().find((e) => e.type === "provider");
        const clears = m.events.some((e) => e.type !== "provider") || (lastProvider && (lastProvider.payload as { type?: string }).type === "assistant");
        return { ...s, events: merge(s.events, m.events), partial: clears ? "" : s.partial };
      }
      if (m.type === "truncate") return { ...s, events: s.events.filter((e) => e.seq <= m.lastSeq), partial: "" };
      const ev = m.event;
      if (ev.seq === -1) {
        // transient partial chunk
        const p = ev.payload as { event?: { type?: string; delta?: { type?: string; text?: string } } };
        const d = p.event;
        if (d?.type === "content_block_delta" && d.delta?.type === "text_delta" && d.delta.text) return { ...s, partial: s.partial + d.delta.text };
        if (d?.type === "message_start") return { ...s, partial: "" };
        return s;
      }
      const isAssistant = ev.type === "provider" && (ev.payload as { type?: string }).type === "assistant";
      const events = merge(s.events, [ev]);
      return events === s.events ? s : { ...s, events, partial: isAssistant || ev.type !== "provider" ? "" : s.partial };
    }
  }
}

/** Subscribes to /ws/chat/:id with automatic reconnect and `since` replay. `tail`: open long chats on their last turns. */
export function useChatStream(chatId: string | null, opts: { tail?: boolean } = {}) {
  const [state, dispatch] = useReducer(reduce, { chat: null, events: [], queue: [], partial: "", connected: false, error: null, from: 0 });
  const tail = !!opts.tail;
  const lastSeq = useRef(0);
  const qc = useQueryClient();

  useEffect(() => {
    dispatch({ type: "reset" });
    lastSeq.current = 0;
    if (!chatId) return;
    let ws: WebSocket | null = null;
    let closed = false;
    let retry = 500;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let pending: ChatEvent[] = [];
    let frame = 0;
    const flush = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      if (!pending.length || closed) return;
      const events = pending;
      pending = [];
      dispatch({ type: "msg", msg: { type: "events", events } });
    };
    const report = () => { if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "visibility", visible: document.visibilityState === "visible" && document.hasFocus() })); };
    const connect = () => {
      ws = new WebSocket(wsUrl(`/ws/chat/${chatId}?since=${lastSeq.current}${tail ? "&tail=1" : ""}`));
      ws.onopen = () => {
        retry = 500;
        dispatch({ type: "conn", connected: true });
        report();
      };
      ws.onmessage = (e) => {
        if (closed) return; // a message from the previous chat's socket must not land in this one
        let msg: ChatWsServerMessage;
        try { msg = JSON.parse(e.data as string) as ChatWsServerMessage; } catch { return; }
        // The chat ends before what we have: it was rewound while this socket was away (the truncate never arrived).
        // Drop what is gone and reconnect from there, or the new events that reuse those numbers are taken as known.
        if (msg.type === "chat" && msg.chat.lastSeq < lastSeq.current) {
          lastSeq.current = msg.chat.lastSeq;
          dispatch({ type: "msg", msg: { type: "truncate", lastSeq: msg.chat.lastSeq } });
          ws?.close();
          return;
        }
        if (msg.type === "event" && msg.event.seq > lastSeq.current) lastSeq.current = msg.event.seq;
        if (msg.type === "events") for (const ev of msg.events) if (ev.seq > lastSeq.current) lastSeq.current = ev.seq;
        // live events are applied once per frame, not one render each; anything else first flushes them, in order
        if (msg.type === "event" && msg.event.seq !== -1) {
          pending.push(msg.event);
          if (!frame) frame = requestAnimationFrame(flush);
          return;
        }
        flush();
        if (msg.type === "truncate") lastSeq.current = Math.min(lastSeq.current, msg.lastSeq);
        if (msg.type === "chat") qc.setQueryData(["chats", msg.chat.projectId, "list"], (old: Chat[] | undefined) => old?.map((c) => (c.id === msg.chat.id ? msg.chat : c)));
        dispatch({ type: "msg", msg });
      };
      ws.onclose = (e) => {
        dispatch({ type: "conn", connected: false });
        if (closed || e.code === 4401 || e.code === 4403) return;
        timer = setTimeout(connect, retry);
        retry = Math.min(retry * 2, 10_000);
      };
    };
    connect();
    // Tell the server whether this chat is actually on screen (visible and focused): push notifications are skipped
    // only then. Repeated every 20 s, so a page iOS froze without a word stops counting within the server's TTL.
    const onVis = () => report();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    window.addEventListener("blur", onVis);
    const beat = setInterval(onVis, 20_000);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
      window.removeEventListener("blur", onVis);
      clearInterval(beat);
      closed = true;
      if (frame) cancelAnimationFrame(frame);
      clearTimeout(timer);
      ws?.close();
    };
  }, [chatId, qc, tail]);

  /** Local change shown at once (optimistic); the server's next chat message replaces it. */
  const patchChat = useCallback((fn: (c: Chat) => Chat) => dispatch({ type: "patch", fn }), []);
  const loadingEarlier = useRef(false);
  /** Loads the turns before what is shown (a long conversation opened on its last turns). */
  const loadEarlier = useCallback(async () => {
    if (!chatId || !state.from || loadingEarlier.current) return;
    loadingEarlier.current = true;
    try {
      const page = await chatsApi.eventsBefore(chatId, state.from);
      dispatch({ type: "earlier", from: page.from, events: page.events });
    } finally {
      loadingEarlier.current = false;
    }
  }, [chatId, state.from]);
  return { ...state, patchChat, loadEarlier };
}
